import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useQuery } from '@tanstack/react-query';
import showToast from '@/components/ui/Toast';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/authService';
import { communityHostKeys } from '@/types/communityHost';
import { openExternalUrl, openMailto } from '@/utils/openExternalUrl';
import { clientPaths } from '@/utils/clientPaths';

const ClientAccountSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { data: coHostCommunities } = useQuery({
    queryKey: communityHostKeys.communities,
    queryFn: () => authService.listCoHostCommunities(),
    enabled: user?.role === 'customer',
  });
  const showManageCommunities = (coHostCommunities?.length ?? 0) > 0;

  const handleLogout = () => {
    logout();
    showToast.success('Berhasil keluar');
    navigate('/');
  };

  return (
    <div className="bg-sporto-bg h-auto text-sporto-dark">
      <div className="max-w-2xl mx-auto px-4 pt-4 md:pt-6 space-y-4">
        {/* Desktop Top Bar Header */}
        <div className="hidden md:flex items-center gap-3 py-2">
          <button
            type="button"
            onClick={() => navigate('/profile')}
            className="w-9 h-9 rounded-full bg-white border border-[#E0E6ED] flex items-center justify-center text-[#0E1726] hover:bg-gray-50 transition-colors cursor-pointer shadow-2xs"
          >
            <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
          </button>
          <h1 className="text-lg md:text-xl font-bold text-[#0E1726]">Pengaturan Akun</h1>
        </div>

        {/* Group 1 Menu Options Card */}
        <div className="bg-white rounded-3xl border border-[#E0E6ED] shadow-2xs divide-y divide-gray-100 overflow-hidden">
          {/* 1. Riwayat Transaksi */}
          <button
            type="button"
            onClick={() => navigate('/transactions')}
            className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50/80 transition-colors cursor-pointer group active:scale-[0.99]"
          >
            <div className="flex items-center gap-3.5 min-w-0">
              <div className="w-11 h-11 rounded-2xl bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
                <Icon icon="ph:receipt-bold" className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-[#0E1726] truncate">Riwayat Transaksi</h3>
                <p className="text-xs text-gray-400 font-medium truncate mt-0.5">Pembayaran booking & event</p>
              </div>
            </div>
            <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-gray-300 group-hover:text-gray-400 transition-colors shrink-0" />
          </button>

          {/* 2. Ganti Password */}
          <button
            type="button"
            onClick={() => navigate('/profile/password')}
            className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50/80 transition-colors cursor-pointer group active:scale-[0.99]"
          >
            <div className="flex items-center gap-3.5 min-w-0">
              <div className="w-11 h-11 rounded-2xl bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
                <Icon icon="ph:lock-key-bold" className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-[#0E1726] truncate">Ganti Password</h3>
                <p className="text-xs text-gray-400 font-medium truncate mt-0.5">Perbarui kata sandi akun</p>
              </div>
            </div>
            <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-gray-300 group-hover:text-gray-400 transition-colors shrink-0" />
          </button>

          {/* 3. Kebijakan Privasi */}
          <button
            type="button"
            onClick={() => {
              const lpUrl = (import.meta.env.VITE_LP_URL || 'http://localhost:3000').replace(/\/$/, '');
              void openExternalUrl(`${lpUrl}/privacy-policy`);
            }}
            className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50/80 transition-colors cursor-pointer group active:scale-[0.99]"
          >
            <div className="flex items-center gap-3.5 min-w-0">
              <div className="w-11 h-11 rounded-2xl bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
                <Icon icon="ph:shield-check-bold" className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-[#0E1726] truncate">Kebijakan Privasi</h3>
                <p className="text-xs text-gray-400 font-medium truncate mt-0.5">Bagaimana data kamu dipakai</p>
              </div>
            </div>
            <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-gray-300 group-hover:text-gray-400 transition-colors shrink-0" />
          </button>

          {/* 4. Syarat & Ketentuan */}
          <button
            type="button"
            onClick={() => {
              const lpUrl = (import.meta.env.VITE_LP_URL || 'http://localhost:3000').replace(/\/$/, '');
              void openExternalUrl(`${lpUrl}/tos`);
            }}
            className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50/80 transition-colors cursor-pointer group active:scale-[0.99]"
          >
            <div className="flex items-center gap-3.5 min-w-0">
              <div className="w-11 h-11 rounded-2xl bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
                <Icon icon="ph:file-text-bold" className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-[#0E1726] truncate">Syarat & Ketentuan</h3>
                <p className="text-xs text-gray-400 font-medium truncate mt-0.5">Aturan penggunaan Sporto</p>
              </div>
            </div>
            <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-gray-300 group-hover:text-gray-400 transition-colors shrink-0" />
          </button>

          {/* 5. Bantuan */}
          <button
            type="button"
            onClick={() => openMailto('mailto:hi@sporto.id')}
            className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50/80 transition-colors cursor-pointer group active:scale-[0.99]"
          >
            <div className="flex items-center gap-3.5 min-w-0">
              <div className="w-11 h-11 rounded-2xl bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
                <Icon icon="ph:question-bold" className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-[#0E1726] truncate">Bantuan</h3>
                <p className="text-xs text-gray-400 font-medium truncate mt-0.5">FAQ & hubungi tim support</p>
              </div>
            </div>
            <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-gray-300 group-hover:text-gray-400 transition-colors shrink-0" />
          </button>

          {showManageCommunities && (
            <button
              type="button"
              onClick={() => navigate(clientPaths.profile.manageCommunities)}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50/80 transition-colors cursor-pointer group active:scale-[0.99]"
            >
              <div className="flex items-center gap-3.5 min-w-0">
                <div className="w-11 h-11 rounded-2xl bg-orange-50 text-[#FC480D] flex items-center justify-center shrink-0">
                  <Icon icon="ph:users-three-bold" className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-bold text-[#0E1726] truncate">Kelola Komunitas</h3>
                  <p className="text-xs text-gray-400 font-medium truncate mt-0.5">Masuk sebagai co-host</p>
                </div>
              </div>
              <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-gray-300 group-hover:text-gray-400 transition-colors shrink-0" />
            </button>
          )}

          {/* 6. Hapus Akun */}
          <button
            type="button"
            onClick={() => navigate('/profile/account/delete')}
            className="w-full p-4 flex items-center justify-between text-left hover:bg-red-50/40 transition-colors cursor-pointer group active:scale-[0.99]"
          >
            <div className="flex items-center gap-3.5 min-w-0">
              <div className="w-11 h-11 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center shrink-0">
                <Icon icon="ph:user-minus-bold" className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-red-500 truncate">Hapus Akun</h3>
                <p className="text-xs text-gray-400 font-medium truncate mt-0.5">Hapus permanen data kamu</p>
              </div>
            </div>
            <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-gray-300 group-hover:text-gray-400 transition-colors shrink-0" />
          </button>
        </div>

        {/* Group 2 Logout Card */}
        <div className="bg-white rounded-3xl border border-[#E0E6ED] shadow-2xs overflow-hidden">
          <button
            type="button"
            onClick={handleLogout}
            className="w-full p-4 flex items-center gap-3.5 text-left hover:bg-red-50/40 transition-colors cursor-pointer group active:scale-[0.99]"
          >
            <div className="w-11 h-11 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center shrink-0">
              <Icon icon="ph:sign-out-bold" className="w-5 h-5" />
            </div>
            <span className="text-sm font-bold text-red-500">Logout</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientAccountSettingsPage;
